import { acceptHMRUpdate, defineStore } from 'pinia';

import { PropertyOnboardingType } from '@tallo/investor/navigation';
import { usePropertyStore } from '@tallo/investor/property';

import {
  PropertyOnboardingStage,
  PropertyOnboardingStageMetaData,
  PropertyOnboardingStep,
} from '../configs/stages.enum';
import { propertyOnboardingRoutes } from '../property-onboarding.routes';
import { getPropertyOnboardingStepRouteName } from '../utils/get-property-onboarding-step-route-name.util';
import { stepValidations } from '../utils/step-validations';

interface PropertyOnboardingStaste {}

export const usePropertyOnboardingStore = defineStore({
  id: 'propertyOnboarding',
  state: (): PropertyOnboardingStaste => ({}),
  getters: {
    getStageRoutes() {
      return (propertyOnboardingRoutes.children || []).filter((route) => {
        return Object.values(PropertyOnboardingStage).includes(route.path as PropertyOnboardingStage);
      });
    },

    getStagesWithMetaData(): PropertyOnboardingStageMetaData[] {
      return this.getStageRoutes.map((route) => route.meta as unknown as PropertyOnboardingStageMetaData);
    },

    getStages(): PropertyOnboardingStage[] {
      return this.getStagesWithMetaData.map(({ stageName }) => stageName);
    },

    getStageSteps() {
      return (stageName: PropertyOnboardingStage): PropertyOnboardingStep[] => {
        const stageRoute = (propertyOnboardingRoutes.children || []).find((stageRoute) => {
          return stageRoute.meta?.stageName === stageName;
        });

        return stageRoute?.children?.map((stepRoute) => stepRoute.meta?.stepName as PropertyOnboardingStep) || [];
      };
    },

    isStageCompteled() {
      return (stageName: PropertyOnboardingStage) => {
        const steps = this.getStageSteps(stageName);
        const stepsCount = steps?.length;

        if (!steps || !stepsCount) {
          return false;
        }

        const completedSteps = steps.map((step) => this.isStepCompleted(step)).filter((isCompleted) => isCompleted);

        return completedSteps.length === stepsCount;
      };
    },

    isStepCompleted() {
      return (stepName: PropertyOnboardingStep) => {
        const { property } = usePropertyStore();

        return stepValidations[stepName](property);
      };
    },
  },
  actions: {
    isFirstStage(stage: PropertyOnboardingStage) {
      return stage === this.getStages[0];
    },

    isLastStage(stage: PropertyOnboardingStage) {
      return stage === this.getStages[this.getStages.length - 1];
    },

    isFirstStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep) {
      const steps = this.getStageSteps(stage);

      return steps[0] === step;
    },

    isLastStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep) {
      const steps = this.getStageSteps(stage);

      return steps[steps.length - 1] === step;
    },

    getNextStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep): PropertyOnboardingStep {
      const steps = this.getStageSteps(stage);
      const currentStepIndex = steps.findIndex((name) => name === step);

      return steps[currentStepIndex + 1];
    },

    getPrevStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep): PropertyOnboardingStep {
      const steps = this.getStageSteps(stage);
      const currentStepIndex = steps.findIndex((name) => name === step);

      return steps[currentStepIndex - 1];
    },

    navigateToStep(step: PropertyOnboardingStep) {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.property?.id;

      return this.router.push({
        name: getPropertyOnboardingStepRouteName(step),
        params: {
          propertyId: propertyId ?? PropertyOnboardingType.NewProperty,
        },
      });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePropertyOnboardingStore, import.meta.hot));
}
